import React from 'react';
import moment from 'moment';
import { formatWholeNumberNaN, formatCurrency2SigFig } from '../../../utils/campaigns';
import { dateFormat } from '../../../../platform/ux/Tasks/configs/designTokens';

const getSignUpsTag = (generatedTag) => {
  const start = `">`;
  const end = '</script>';
  const pos = generatedTag.indexOf(start) + start.length;
  return generatedTag.substring(pos, generatedTag.indexOf(end, pos));
};

const TagsDetailsController = ({
  data,
  getBasicTagActivity,
  basicTagActivityData,
  persistedTagActivityData,
  tagActivityLoading,
  deleteTag,
  disableTag,
  disableEnableLoading,
  regenerateTag,
  userPermission,
  carts,
  dsps,
  regenerateImpressionsTag,
  getPageUrlActivity,
  pageUrlActivityRows,
  pageUrlActivityLoading,
  ...props
}) => {
  const { eventsTarget } = data;
  const getCarts = (carts && carts.getCarts) || [];
  const { getDSP } = dsps;

  const cart = getCarts.find((cart) => cart.value === eventsTarget.cart) || {
    label: 'Unsupported Cart',
    value: 'thirdparty', //Mark unsupported carts as thirdparty
    eventsTracked: [],
  };

  const details = {
    id: eventsTarget.id,
    name: eventsTarget.name,
    appId: eventsTarget.eventTags[0].appId[0],
    cartLabel: cart.label,
    cart: cart.value,
    advertiser: eventsTarget.orgs[0].name,
    supportMeasurements: cart.eventsTracked,
    isDisabled: eventsTarget.isDisabled,
    isPublisherDomain: eventsTarget.isPublisherDomain,
    publisherDomain: eventsTarget.domain,
    isAdvertiser: eventsTarget.isAdvertiser,
    impressionEnvironments: eventsTarget.impressionEnvironments,
    dsp: eventsTarget.dsp,
  };

  const parentChannelPartner = eventsTarget.orgs[0].parentOrg.find(
    (org) => org.level === 'CHANNEL_PARTNER' && org.tagDomain !== null
  );
  
  // Use eventsTarget.domain as the primary source for the domain
  // This ensures that when the tag is regenerated with a new domain, it's properly reflected
  if (eventsTarget.domain) {
    details.domain = eventsTarget.domain;
  } else if (eventsTarget.orgs[0].level === 'CHANNEL_PARTNER') {
    details.domain = eventsTarget.orgs[0].tagDomain;
  } else if (parentChannelPartner) {
    details.domain = parentChannelPartner.tagDomain;
  }

  const testLogs = eventsTarget.testLogs.map((log) => {
    return {
      date: moment(log.testDate).format(dateFormat.full),
      pageViews: formatWholeNumberNaN(log.pageViews),
      transactions: formatWholeNumberNaN(log.transactions),
      basketItems: formatWholeNumberNaN(log.basketItems),
      signups: formatWholeNumberNaN(log.signups),
      total: formatCurrency2SigFig(log.total),
      sessions: formatWholeNumberNaN(log.sessions),
      impressions: formatWholeNumberNaN(log.impressions),
      retailIds: log.retailIds || [],
    };
  });
  testLogs.reverse();
  const latestTestLog = testLogs[0];
  const impressionsFromTestLogs = latestTestLog?.impressions || 0;
  const latestRetailIds = latestTestLog?.retailIds || [];

  const generatedTag = {
    date: eventsTarget.tagUpdateDate,
    transactions: eventsTarget.transactions,
    pageViews: eventsTarget.pageViews,
    basketItems: eventsTarget.basketItems,
    tag: eventsTarget.tag,
    cart: eventsTarget.cart,
    total: eventsTarget.total,
    sessions: eventsTarget.sessions,
    impressions: impressionsFromTestLogs,
    latestRetailIds: latestRetailIds,
    retailIds: eventsTarget.retailIds || [],
  };

  const signUpsTag = {
    date: eventsTarget.tagUpdateDate,
    signups: eventsTarget.signups,
    tag: getSignUpsTag(eventsTarget.tag ? eventsTarget.tag.generatedTag : ''),
    hasSignups: eventsTarget.isSignUps, // Flag for newly created tag to mark it as a sign up tag
  };

  const testTag = async (retailId) => {
    try {
      await getBasicTagActivity({
        variables: {
          appId: eventsTarget.eventTags[0].appId[0],
          orgId: eventsTarget.orgs[0].id,
          tagId: eventsTarget.id,
          retailID: retailId,
        },
      });
    } catch (error) {
      console.error('Error in getBasicTagActivity:', error);
      throw error;
    }
  };

  const fetchPageUrlActivity = async () => {
    try {
      await getPageUrlActivity({
        variables: {
          appId: eventsTarget.eventTags[0].appId[0],
          daysToTrack: 5,
        },
      });
    } catch (error) {
      console.error('Error in getPageUrlActivity:', error);
      throw error;
    }
  };

  const onDisableEnableTag = async (status) => {
    await disableTag({
      variables: {
        id: eventsTarget.id,
        isDisabled: status,
      },
    });
  };

  const regenerateTagEvent = async (values, type) => {
    if (type === 'impression') {
      await regenerateImpressionsTag({
        variables: {
          id: eventsTarget.id,
          appId: values.appId,
          environment: values.environment,
        },
      });
    } else {
      await regenerateTag({
        variables: {
          id: eventsTarget.id,
          domain: values.domain,
          cart: values.cart,
        },
      });
    }
  };

  return React.cloneElement(props.children, {
    ...props.children.props,
    details,
    generatedTag,
    signUpsTag,
    testTag,
    testLogs,
    basicTagActivityData,
    persistedTagActivityData,
    tagActivityLoading,
    deleteTag,
    onDisableEnableTag,
    disableEnableLoading,
    regenerateTag: regenerateTagEvent,
    userPermission,
    carts: getCarts,
    dsps: getDSP,
    fetchPageUrlActivity,
    pageUrlActivityRows,
    pageUrlActivityLoading,
  });
};

export default TagsDetailsController;
